<script lang="ts" setup>
  export type ScoringRow = { outcome: string; score: string; tier: 'penalty' | 1 | 2 | 3 };

  defineProps<{ title: string; scoringLabel: string; scoringRows: ScoringRow[]; outcomeWidth: number }>();
</script>

<template>
  <div class="page">
    <h2>{{ title }}</h2>
    <slot />
    <p class="scoring-label">{{ scoringLabel }}</p>
    <table class="scoring-table" :style="{ '--outcome-width': `${outcomeWidth}%` }">
      <tbody>
        <tr v-for="row in scoringRows" :key="row.outcome">
          <td>{{ row.outcome }}</td>
          <td class="score" :class="`score-${row.tier}`">
            <span v-if="row.tier !== 'penalty'" class="invisible-sign">−</span>{{ row.score }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
  .page {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  h2 {
    color: var(--color-primary);
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-strong);
    letter-spacing: 0.05em;
    margin: 0;
    text-transform: uppercase;
  }

  .page :slotted(p) {
    color: var(--color-text);
    font-size: var(--font-size-sm);
    line-height: 1.5;
    margin: 0;
  }

  .scoring-label {
    color: var(--color-primary);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-strong);
    letter-spacing: 0.05em;
    line-height: 1.5;
    margin: 0 0 -0.5rem;
    text-transform: uppercase;
  }

  .score {
    font-weight: var(--font-weight-strong);
    text-align: right;
  }

  .score-1 {
    color: var(--color-primary);
  }

  .score-2 {
    color: color-mix(in srgb, var(--color-primary), var(--color-tint) 20%);
  }

  .score-3 {
    color: color-mix(in srgb, var(--color-primary), var(--color-tint) 40%);
  }

  .score-penalty {
    color: var(--color-penalty);
  }

  .scoring-table {
    border-collapse: collapse;
    color: var(--color-text);
    font-size: var(--font-size-xs);
    line-height: 1.5;
    table-layout: fixed;
    width: 100%;
  }

  .scoring-table td {
    padding: 0.2rem 0;
  }

  .scoring-table td:first-child {
    width: var(--outcome-width);
  }

  .invisible-sign {
    visibility: hidden;
  }

  .scoring-table td:last-child {
    text-align: left;
  }
</style>
